import React from "react";

// App-wide error boundary. Without one, any uncaught render error unmounts the
// whole React root (createRoot) and the user just sees a blank page. This is
// especially easy to hit in Safari, which throws on things Chrome tolerates
// (e.g. `new Date("2024-01-01 12:00")` is Invalid Date -> RangeError on
// toISOString). Here we catch it, log the full error + component stack to the
// console (so it's visible in Safari's Web Inspector), and show a readable
// fallback with a reload instead of a white screen.
//
// Fallback colors use the design-system CSS variables with literal dark
// fallbacks, so it still renders even if the design-system failed to load.

type Props = {
  children: React.ReactNode;
};

type State = {
  error: Error | null;
  componentStack: string | null;
};

class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the raw error in the console so it's grabbable from Safari's Web
    // Inspector with the exact file:line that threw.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Uncaught render error:", error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={styles.screen}>
        <div style={styles.card}>
          <div style={styles.title}>Something went wrong</div>
          <div style={styles.subtitle}>
            The app hit an unexpected error and stopped rendering. Reloading
            usually recovers. If it keeps happening, the details below help us
            fix it.
          </div>

          <div style={styles.errorName}>
            {error.name}: {error.message}
          </div>

          {(error.stack || componentStack) && (
            <pre style={styles.stack}>
              {error.stack || ""}
              {componentStack ? `\n\nComponent stack:${componentStack}` : ""}
            </pre>
          )}

          <button type="button" style={styles.button} onClick={this.handleReload}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}

const styles: Record<string, React.CSSProperties> = {
  screen: {
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    boxSizing: "border-box",
    background: "var(--color-bg, #111111)",
    color: "var(--color-text, #ffffff)",
    fontFamily: "var(--font-sans, -apple-system, BlinkMacSystemFont, sans-serif)",
  },
  card: {
    width: "100%",
    maxWidth: "640px",
    background: "var(--color-surface, #1a1a1a)",
    border: "1px solid var(--color-border, #333333)",
    borderRadius: "var(--radius-lg, 12px)",
    padding: "32px",
    boxSizing: "border-box",
  },
  title: {
    fontSize: "20px",
    fontWeight: 600,
    marginBottom: "8px",
  },
  subtitle: {
    fontSize: "14px",
    lineHeight: 1.5,
    color: "var(--color-text-secondary, #aaaaaa)",
    marginBottom: "20px",
  },
  errorName: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#ff6b6b",
    marginBottom: "12px",
    wordBreak: "break-word",
  },
  stack: {
    fontSize: "12px",
    lineHeight: 1.5,
    color: "var(--color-text-secondary, #aaaaaa)",
    background: "var(--color-bg, #111111)",
    border: "1px solid var(--color-border, #333333)",
    borderRadius: "8px",
    padding: "12px",
    maxHeight: "240px",
    overflow: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    marginBottom: "20px",
  },
  button: {
    appearance: "none",
    border: "none",
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    background: "var(--color-accent, #ffffff)",
    color: "var(--color-bg, #111111)",
  },
};

export default ErrorBoundary;
